import React, { useEffect, useMemo, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { CustomerServicePicker } from "./CustomerServicePicker";
import { DateTimeField, FIELD_CLASS, FormErrors, RequestError } from "./MaintenanceFormFields";
import {
	buildWindowPayload,
	composeDescriptionPreview,
	EMPTY_WINDOW_FORM,
	validateWindowForm,
	type MaintenanceWindowForm,
} from "./maintenanceWindowsModel";
import { createMaintenanceWindow, describeRequestError } from "./requests/maintenanceWindowsApi";
import type { MaintenanceRecordRow, TenantOption } from "./types";

/**
 * What one customer will read, before anything is saved. The operator picks
 * whose copy to look at, because each copy lists only its own services.
 */
function DescriptionPreview({ form }: { form: MaintenanceWindowForm }) {
	const [tenantId, setTenantId] = useState<number | null>(null);
	const tenant =
		form.tenants.find((candidate) => candidate.id === tenantId) ?? form.tenants[0] ?? null;
	const text = tenant ? composeDescriptionPreview(form, tenant) : null;

	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-sm font-medium text-semantic-text-primary">
					Preview — what the customer will read
				</span>
				{form.tenants.length > 1 && tenant && (
					<select
						aria-label="Customer to preview"
						value={tenant.id}
						onChange={(event) => setTenantId(Number(event.target.value))}
						className={`${FIELD_CLASS} w-auto max-w-[260px]`}
					>
						{form.tenants.map((candidate) => (
							<option key={candidate.id} value={candidate.id}>
								{candidate.name}
							</option>
						))}
					</select>
				)}
			</div>
			{text ? (
				<pre className="whitespace-pre-wrap font-body text-sm rounded-sm border border-semantic-border-primary bg-semantic-bg-secondary px-4 py-3 text-semantic-text-primary">
					{text}
				</pre>
			) : (
				<p className="text-sm text-semantic-text-secondary">
					Select a customer and valid start and end times to see the preview.
				</p>
			)}
		</div>
	);
}

/**
 * Announce a scheduled maintenance window (CTX-1294).
 *
 * Saving writes one maintenance record per selected customer; each reads a
 * description that lists only its own services. From then on every copy is
 * edited or deleted on its own — there is no window to edit as a whole.
 */
const CreateMaintenanceWindowModal: React.FC<{
	isOpen: boolean;
	tenantOptions: TenantOption[];
	tenantsLoading: boolean;
	onClose: () => void;
	onCreated: (created: MaintenanceRecordRow[]) => void;
}> = ({ isOpen, tenantOptions, tenantsLoading, onClose, onCreated }) => {
	const api = useAxiosAuth0();
	const [form, setForm] = useState<MaintenanceWindowForm>(EMPTY_WINDOW_FORM);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Validation messages wait for the first save attempt: a form that opens
	// already listing six errors reads as broken, not as empty.
	const [attempted, setAttempted] = useState(false);

	useEffect(() => {
		if (!isOpen) return;
		setForm(EMPTY_WINDOW_FORM);
		setError(null);
		setAttempted(false);
	}, [isOpen]);

	const errors = useMemo(() => validateWindowForm(form), [form]);

	const update = <K extends keyof MaintenanceWindowForm>(
		field: K,
		value: MaintenanceWindowForm[K]
	) => setForm((prev) => ({ ...prev, [field]: value }));

	const handleSave = async () => {
		setAttempted(true);
		if (errors.length > 0) return;
		setSubmitting(true);
		setError(null);
		try {
			const created = await createMaintenanceWindow(api, buildWindowPayload(form));
			onCreated(created);
			onClose();
		} catch (err) {
			setError(describeRequestError(err));
		} finally {
			setSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<GenericModal
			isOpen
			onClose={onClose}
			size="lg"
			title={
				<div className="flex flex-col min-w-0">
					<span className="text-h5 font-bold">New maintenance window</span>
					<span className="text-sm font-normal text-semantic-text-secondary mt-0.5">
						Shown to each selected customer under Maintenance
					</span>
				</div>
			}
			footer={
				<div className="flex items-center gap-2 w-full">
					<div className="flex-1" />
					<GenericButton
						label="Cancel"
						variant="secondary"
						size="md"
						onClick={onClose}
						disabled={submitting}
					/>
					<GenericButton
						label={
							form.tenants.length > 1
								? `Create for ${form.tenants.length} customers`
								: "Create window"
						}
						variant="brand"
						size="md"
						loading={submitting}
						disabled={submitting || (attempted && errors.length > 0)}
						onClick={() => {
							void handleSave();
						}}
					/>
				</div>
			}
		>
			<div className="flex flex-col gap-5">
				<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-4">
					<legend className="px-1 text-sm font-bold">Notice</legend>
					<GenericInput
						label="Title"
						placeholder="Internet infrastructure optimization"
						value={form.title}
						onChange={(value) => update("title", value)}
					/>
					<div className="flex flex-col space-y-2">
						<label
							htmlFor="maintenance-message"
							className="text-sm font-medium text-semantic-text-primary"
						>
							Message for the customer
						</label>
						<textarea
							id="maintenance-message"
							rows={3}
							value={form.message}
							onChange={(event) => update("message", event.target.value)}
							placeholder="Brief, non-continuous interruptions may occur during the window."
							className={FIELD_CLASS}
						/>
					</div>
				</fieldset>

				<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-4">
					<legend className="px-1 text-sm font-bold">Window</legend>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
						<DateTimeField
							id="maintenance-starts-at"
							label="Starts"
							value={form.startsAtLocal}
							onChange={(value) => update("startsAtLocal", value)}
						/>
						<DateTimeField
							id="maintenance-ends-at"
							label="Ends"
							value={form.endsAtLocal}
							onChange={(value) => update("endsAtLocal", value)}
						/>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
						<GenericInput
							label="Expected impact"
							type="number"
							unit="minutes"
							placeholder="10"
							value={form.expectedImpactMinutes}
							onChange={(value) => update("expectedImpactMinutes", value)}
						/>
					</div>
				</fieldset>

				<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-4">
					<legend className="px-1 text-sm font-bold">Affected customers and services</legend>
					<CustomerServicePicker
						tenantOptions={tenantOptions}
						tenantsLoading={tenantsLoading}
						selectedTenants={form.tenants}
						selectedServices={form.services}
						onChange={({ tenants, services }) =>
							setForm((prev) => ({ ...prev, tenants, services }))
						}
					/>
				</fieldset>

				<DescriptionPreview form={form} />

				{attempted && <FormErrors errors={errors} />}
				<RequestError message={error} />
			</div>
		</GenericModal>
	);
};

export default CreateMaintenanceWindowModal;
